import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  UserKeys,
  addMemberToDelta,
  addMemberToGroup,
  getUserGroups,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { Group, Page } from '../../../../../types';
import GeneralDialog from '../../../../shared/general-dialog';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import AvatarChip from '../../../../shared/avatar-chip';
import { useUserSettings } from '../../../../utils/use-user-settings';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Grow from '@mui/material/Grow';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
interface CompareGroupsProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  data: Page<Group[]> | undefined;
  comparedUser: string;
  setComparedUser: Dispatch<SetStateAction<string>>;
}

export default function CompareGroupsDialog({
  open,
  handleClose,
  userEmail,
  data,
  comparedUser,
  setComparedUser,
}: CompareGroupsProps) {
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const [isDeltaLoading, setIsDeltaLoading] = useState(false);
  const [isCopyLoading, setIsCopyLoading] = useState(false);
  const [isDelta, setIsDelta] = useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { changeLogRoles } = useUserSettings();
  const [previousComparedUser, setPreviousComparedUser] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [queryData] = useState<{
    cursor: string | null;
    results: Group[];
    nameSpace: string;
    firstPull: boolean;
    searchTime: number;
    pageSize: number;
  }>({
    cursor: null,
    results: [],
    nameSpace: '',
    firstPull: true,
    searchTime: 0,
    pageSize: 100,
  });

  const isEnabled = comparedUser.length > 0;
  const { data: groups, isFetching } = useQuery({
    queryKey: [UserKeys.UserGroups, comparedUser, queryData],
    queryFn: () => getUserGroups(comparedUser, queryData),
    enabled: isEnabled,
    meta: {
      errorMessage:
        'There was an issue loading the users group data. Please refresh and try again.',
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      isDelta
        ? addMemberToDelta(
            userEmail,
            selectedGroupIds,
            'MEMBER',
            comparedUser,
            possibleDeltaGroups,
            changeLogMessage
          )
        : addMemberToGroup(
            userEmail,
            selectedGroupIds,
            'MEMBER',
            changeLogMessage
          ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: isDelta
          ? 'The task to combine the user groups has started.'
          : 'The task to copy the user groups has started.',
      });
      handleClose();
      setAutoCompleteResponse('');
      setComparedUser('');
      setPreviousComparedUser('');
      setSelectedGroupIds([]);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: isDelta
          ? 'There was an issue combining the users groups. Please refresh and try again.'
          : 'There was an issue copying the users groups. Please refresh and try again.',
      });
    },
    onSettled: () => {
      setChangeLogMessage('');
      if (isDelta) {
        createEvent(
          analytics.user_management.user_details.groups.combine_groups,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.groups,
          }
        );
      } else {
        createEvent(analytics.user_management.user_details.groups.copy_groups, {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.groups,
        });
      }
      setIsDelta(false);
      setIsCopyLoading(false);
      setIsDeltaLoading(false);
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, UserRoles.AddGroupMembers.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const rows =
    data?.results.map((group) => {
      return {
        id: group.email,
        groupEmail: group.email,
        groupName: group.name,
      };
    }) || [];

  const firstTableIds = data?.results.map((group) => group.email) || [];
  const secondTablesIds = groups?.results.map((group) => group.email) || [];
  const possibleDeltaGroups = firstTableIds.filter(
    (group) => secondTablesIds.length > 0 && !secondTablesIds.includes(group)
  );

  const possibleCopyGroups = secondTablesIds.filter(
    (group) => !firstTableIds.includes(group)
  );
  if (
    comparedUser &&
    comparedUser !== previousComparedUser &&
    groups?.results
  ) {
    const firstTableIds = data?.results.map((group) => group.email) || [];
    const secondTableIds = groups.results.map((group) => group.email) || [];
    const newPossibleCopyGroups = secondTableIds.filter(
      (id) => !firstTableIds.includes(id)
    );
    setSelectedGroupIds(newPossibleCopyGroups);
    setPreviousComparedUser(comparedUser);
  }
  const secondRows =
    groups?.results.map((group) => {
      return {
        id: group.email,
        groupEmail: group.email,
        groupName: group.name,
      };
    }) || [];

  const handleSelectRow = (id: string) => {
    setSelectedGroupIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };
  const toggleSelectAll = () => {
    const selectable = possibleCopyGroups;
    if (selectedGroupIds.length === selectable.length) {
      setSelectedGroupIds([]);
    } else {
      setSelectedGroupIds(selectable);
    }
  };

  const isSelectAllChecked =
    possibleCopyGroups.length > 0 &&
    selectedGroupIds.length === possibleCopyGroups.length;
  return (
    <GeneralDialog
      open={open}
      handleClose={() => {
        handleClose();
        setAutoCompleteResponse('');
        setComparedUser('');
      }}
      title="Compare Groups"
      maxWidth="xl"
      dialogContent={
        <Box sx={{ mt: 2, mb: 1 }}>
          {/* Autocomplete and Search Button */}
          <Box sx={{ width: '49%', mb: 3 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              gap={2}
              alignItems="center"
            >
              <Box sx={{ flex: 1 }}>
                <SharedAutocomplete
                  sources={['users']}
                  autocompleteValue={autoCompleteResponse}
                  setAutoCompleteValue={setAutoCompleteResponse}
                  onSelection={(value) => {
                    setAutoCompleteResponse(value);
                    setComparedUser(value);
                  }}
                />
              </Box>
            </Stack>
          </Box>

          {/* Tables Section */}
          <Stack direction={{ xs: 'column', md: 'row' }} gap={3} mt={3}>
            {/* Current User Table */}
            <Box sx={{ flex: 1, padding: '0 !important' }}>
              <Typography variant="subtitle2" gutterBottom>
                <AvatarChip email={userEmail} type="User" /> – Current Groups
              </Typography>
              <TableContainer sx={{ maxHeight: 260 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Group Email</TableCell>
                      <TableCell align="right">Group Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ padding: '11px 16px!important' }}>
                          {row.groupEmail}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ padding: '11px 16px!important' }}
                        >
                          {row.groupName}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Compared User Table */}
            {secondRows.length > 0 && (
              <Box sx={{ flex: 1, padding: '0 !important' }}>
                <Typography variant="subtitle2" gutterBottom>
                  <AvatarChip email={comparedUser} type="User" /> – Compared
                  Groups
                </Typography>
                <TableContainer sx={{ maxHeight: 260 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelectAllChecked}
                            indeterminate={
                              selectedGroupIds.length > 0 &&
                              selectedGroupIds.length <
                                possibleCopyGroups.length
                            }
                            onChange={toggleSelectAll}
                            disabled={possibleCopyGroups.length === 0}
                          />
                        </TableCell>
                        <TableCell>Group Email</TableCell>
                        <TableCell align="right">Group Name</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {secondRows.map((row) => {
                        const isDisabled = firstTableIds.includes(row.id);
                        return (
                          <TableRow key={row.id}>
                            <TableCell
                              padding="checkbox"
                              sx={{ padding: '0 0 0 7px !important' }}
                            >
                              <Tooltip
                                title={
                                  isDisabled ? 'User is already a member' : ''
                                }
                              >
                                <span>
                                  <Checkbox
                                    value={row.id}
                                    disabled={isDisabled}
                                    checked={selectedGroupIds.includes(row.id)}
                                    onChange={() => handleSelectRow(row.id)}
                                  />
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell>{row.groupEmail}</TableCell>
                            <TableCell align="right">{row.groupName}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Skeleton Loading */}
            {isFetching && !secondRows.length && (
              <Stack flex={1} gap={1}>
                <Skeleton variant="text" sx={{ fontSize: '1.5rem' }} />
                <Skeleton variant="rounded" height={160} />
              </Stack>
            )}
          </Stack>

          {/* Copy Alert Notification */}
          {comparedUser && (
            <Grow in={true}>
              <Alert
                sx={{ maxWidth: 800, mx: 'auto', mt: 5 }}
                severity="info"
                icon={false}
              >
                Copy <strong>{selectedGroupIds.length}</strong> selected
                group(s) from <AvatarChip email={comparedUser} type="User" /> to{' '}
                <AvatarChip email={userEmail} type="User" />.
              </Alert>
            </Grow>
          )}

          {/* Change Log Dialog */}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={(isCopyLoading || isDeltaLoading) && isPending}
          />
        </Box>
      }
      primaryButton={{
        loading: isPending && isCopyLoading,
        action: () => {
          setIsCopyLoading(true);
          setIsDelta(false);
          handleSubmit();
        },
        disabled: isDeltaLoading || selectedGroupIds.length === 0,
        shown: true,
        title: `Copy`,
      }}
      // Revisit
      // primaryButton={{
      //   loading: isPending && isDeltaLoading,
      //   action: () => {
      //     setIsDeltaLoading(true);
      //     setIsDelta(true);
      //     handleSubmit();
      //   },
      //   disabled: isCopyLoading || possibleDeltaGroups.length === 0,
      //   title: 'Combine',
      // }}
    />
  );
}
